import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { createTrade, updateTrade } from "../../../fetchers/cmmsTrade";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
} from "../../cmms/assetHierarchyStyles";
import cmmsMessages from "../../cmms/cmmsMessages";
import FieldError from "../../cmmsSpareParts/components/FieldError";
import messages from "../messages";

/*
 * Add or rename a trade.
 *
 * The name and nothing else. IsActive is not here because retiring a trade has an endpoint of its
 * own, and the members are not here because they have one too — this dialog is about what the
 * group is called, not who is in it.
 */
const emptyForm = { name: "" };

export default function TradeFormDialog({ open, onClose, trade, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const isEdit = Boolean(trade?.id);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  /*
   * Required, not optional. AxionControls.AxionTextBox is uncontrolled — it passes `value` to
   * TextField as defaultValue — so the field is seeded when the dialog mounts and there is no
   * second chance to fill it from a later render.
   */
  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? { name: trade.name ?? "" } : emptyForm);
    setErrors({});
  }, [open, isEdit, trade]);

  const handleTextChange = (event) => {
    const { name, id, value } = event.target;
    setForm((current) => ({ ...current, [name || id]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: (payload) => (isEdit
      ? updateTrade(trade.id, payload)
      : createTrade(payload)),
  });

  /*
   * Name required, and nothing else. Uniqueness is the server's: it holds a unique index on
   * (CompanyKey, Name), inactive trades included, and answers a clash with CMMS_TRADE_NAME_TAKEN
   * and the name already taking it.
   */
  const validate = () => {
    const next = {};
    if (!form.name?.trim()) next.name = intl.formatMessage(messages.validationNameRequired);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      companyKey,
      name: form.name.trim(),
      // No created/updatedByUserId — the server takes it from the token.
    };

    try {
      await saveMutation.mutateAsync(payload);
      // Every filter variant, because a new trade belongs in whichever list is on screen and a
      // rename changes the sort.
      await queryClient.invalidateQueries({ queryKey: ["cmmsTrades"] });
      pushNotification(intl.formatMessage(
        isEdit ? messages.tradeToastUpdated : messages.tradeToastCreated,
      ));
      onClose?.();
    } catch (error) {
      // The server's own words: a duplicate says which name, which no message written in advance
      // could.
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.tradeSaveFailed)));
    }
  };

  const submitting = saveMutation.isPending || saveMutation.isLoading;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(isEdit ? messages.tradeFormEditTitle : messages.tradeFormAddTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box>
          <AxionControls.AxionTextBox
            name="name"
            label={intl.formatMessage(messages.fieldName)}
            value={form.name}
            onChange={handleTextChange}
          />
          <FieldError message={errors.name} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.tradeFieldNameHint} />
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...(isEdit ? cmmsMessages.save : cmmsMessages.add)} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
